import {useState} from "react";
import axios from "axios";

// Models
import {SubscriptionDTO} from "../../../Models/DTOs/Subscription";
import {AuthUser} from "../../../Models/DTOs/AuthUser";

// Api
import {PauseSubscription} from "../../../Api/Stripe";

// Helpers
import {normalizePauseError} from "../../../Helpers/Subscription";
import {toGBDate} from "../../../Helpers/DataTypesGeneral/DateAndTime";

// Components
import SelectInput from "../../Inputs/SelectInput/SelectInput";
import ButtonPill from "../../Buttons/ButtonPill";

// Assets
import IconLoaderCircle from "../../../Assets/SVGs/Icons/Loading/LoaderCircle";

type PauseMembershipProps = {
    sub: SubscriptionDTO | null;
    userData: AuthUser | null;
    getValidAccessToken: () => Promise<string | null>;
};

const PAUSE_OPTIONS = [
    {value: 1, label: "1 month"},
    {value: 3, label: "3 months"},
    {value: 6, label: "6 months"},
];

const PauseMembership = ({sub, userData, getValidAccessToken}: PauseMembershipProps) => {
    const
        [selectedMonths, setSelectedMonths] = useState<number | null>(null),
        [isLoading, setIsLoading] = useState<boolean>(false),
        [error, setError] = useState<string>(""),
        [successMessage, setSuccessMessage] = useState<string>(""),

        handlePause = async () => {
            const userId = userData?.id ?? sub?.UserId ?? "";

            if (!userId) {
                setError("Missing user id.");
                return;
            }

            if (!selectedMonths) {
                setError("Please choose how long to pause.");
                return;
            }

            const controller = new AbortController();

            try {
                setIsLoading(true);
                setError("");

                const token = await getValidAccessToken();
                if (!token) {
                    setError("Your session has expired. Please sign in again.");
                    return;
                }

                const res = await PauseSubscription(userId, selectedMonths, token, controller);

                if (axios.isAxiosError(res)) {
                    console.error("Pause failed:", res);
                    setError(normalizePauseError(res));
                    return;
                }

                if (res.paused) {
                    const accessUntil = toGBDate(res.access_until) ?? "the end of your current period";
                    const resumesAt = toGBDate(res.resumes_at) ?? "your scheduled resume date";
                    const annualNote =
                        sub?.BillingInterval === "year"
                            ? " As an annual member, a new yearly term will be charged when your pause ends."
                            : "";
                    setSuccessMessage(
                        `Your membership is paused. You'll keep access until ${accessUntil}, ` +
                        `then it automatically resumes on ${resumesAt}.${annualNote} You can resume early anytime.`
                    );
                    // Reload so the account screen and auth state reflect the paused status.
                    setTimeout(() => window.location.reload(), 4000);
                } else {
                    setError("Something went wrong, please try again.");
                }
            } catch (e) {
                console.error("Error pausing membership:", e);
                setError("Something went wrong, please try again.");
            } finally {
                setIsLoading(false);
            }
        };

    return (
        <form
            className="form--cancel-or-pause form--pause-membership is-dark-mode"
            onSubmit={(e) => e.preventDefault()}
        >
            <div className="cancel-or-pause__heading-wrapper">
                <h1>Pause Your Membership</h1>
            </div>

            <div className="cancel-or-pause__divider"/>

            <div className="cancel-or-pause__content-wrapper">
                {successMessage ? (
                    <p className="pause-membership__success">{successMessage}</p>
                ) : (
                    <>
                        <p>
                            Pause billing for 1, 3, or 6 months. You'll keep access through the time
                            you've already paid for, then your membership freezes (no billing, no
                            access) until it automatically resumes. You can resume early at any time.
                        </p>

                        {sub?.BillingInterval === "year" && (
                            <p className="pause-membership__notice">
                                You're on the annual plan, so your renewal is deferred while you're
                                paused. When your pause ends, a new yearly term will be charged to your
                                card.
                            </p>
                        )}

                        <SelectInput
                            name="pauseDuration"
                            label="Pause duration"
                            value={selectedMonths}
                            options={PAUSE_OPTIONS}
                            placeholder="Select duration"
                            onChange={(e) => {
                                setError("");
                                setSelectedMonths(
                                    e.target.value == null ? null : Number(e.target.value)
                                );
                            }}
                        />

                        {error && <p className="pause-membership__error">{error}</p>}

                        <ButtonPill
                            label={isLoading ? "" : "Pause Membership"}
                            icon={isLoading ? IconLoaderCircle({className: "icon--loader"}) : null}
                            className={`${isLoading ? "is-loading" : ""} ${!selectedMonths ? "is-not-valid" : ""}`}
                            onClick={handlePause}
                            disabled={isLoading || !selectedMonths}
                        />
                    </>
                )}
            </div>
        </form>
    );
};

export default PauseMembership;
